import type { SearchParams } from '@/hooks/useRouterQuery';

/**
 * The campaign page's one "Connection" filter. Each option asks a different question, so
 * picking one replaces the other. Underneath it still drives two URL params, which the
 * server, saved filter defaults and the analytics cards' click-throughs all use:
 * - `connectionAccepted`: this campaign's own connection request (the same events as the
 *   Connection Requests card).
 * - `connectedUserId`: whether the person is a LinkedIn connection of the viewer, or of
 *   anyone on the team, however that happened.
 */
type ConnectionQuery = Pick<SearchParams, 'connectionAccepted' | 'connectedUserId'>;

export type ConnectionFilterValue = 'accepted' | 'pending' | 'sent' | 'me' | 'anyone';

export const CONNECTION_FILTER_QUERY_KEYS = ['connectionAccepted', 'connectedUserId'] as const;

export const CONNECTION_FILTER_OPTIONS: { value: ConnectionFilterValue; label: string; description: string }[] = [
  {
    value: 'accepted',
    label: 'Connected via this campaign',
    description: 'This campaign sent the request and they accepted',
  },
  { value: 'pending', label: 'Request pending', description: 'This campaign sent the request, not accepted yet' },
  { value: 'sent', label: 'Request sent by this campaign', description: 'Accepted or still pending' },
  { value: 'me', label: 'Connected to me', description: 'Your LinkedIn connection, however it happened' },
  {
    value: 'anyone',
    label: 'Connected to anyone on the team',
    description: 'You or any teammate is connected to them',
  },
];

export function isConnectionFilterValue(value: string): value is ConnectionFilterValue {
  return CONNECTION_FILTER_OPTIONS.some((option) => option.value === value);
}

/**
 * The option a query selects, or null when the filter is off. A teammate's user id (from a
 * shared link or a saved default) has no option of its own and comes back as that id.
 */
export function connectionFilterValue(query: ConnectionQuery, userId: string): string | null {
  if (query.connectionAccepted != null) return query.connectionAccepted;
  if (query.connectedUserId == null) return null;
  if (query.connectedUserId === '__any__') return 'anyone';
  if (query.connectedUserId === userId) return 'me';
  return query.connectedUserId;
}

/** The URL params for an option. Always sets both, so the other question is cleared. */
export function connectionFilterQuery(value: ConnectionFilterValue, userId: string): ConnectionQuery {
  switch (value) {
    case 'accepted':
    case 'pending':
    case 'sent':
      return { connectionAccepted: value, connectedUserId: undefined };
    case 'me':
      return { connectionAccepted: undefined, connectedUserId: userId };
    case 'anyone':
      return { connectionAccepted: undefined, connectedUserId: '__any__' };
  }
}
